import { Game, Resources } from '../game/Game'
import { Unit } from '../game/Unit'
import { Building } from '../game/Building'
import { BUILDING_TYPES } from '../data/buildings'
import { UNIT_TYPES } from '../data/units'
import { Faction } from '../data/factions'
import { AssetLoader } from '../engine/AssetLoader'

export class HUD {
  private game: Game
  private container: HTMLElement

  // UI Elements
  private resourceBar!: HTMLElement
  private minimap!: HTMLCanvasElement
  private minimapCtx!: CanvasRenderingContext2D
  private controlPanel!: HTMLElement
  private unitInfo!: HTMLElement
  private actionButtons!: HTMLElement
  private messageLog!: HTMLElement
  private ageDisplay!: HTMLElement
  private gameOverScreen!: HTMLElement

  constructor(game: Game, container: HTMLElement) {
    this.game = game
    this.container = container

    this.createUI()
    this.setupCallbacks()

    this.minimap = document.getElementById('minimap') as HTMLCanvasElement
    this.minimapCtx = this.minimap.getContext('2d')!
  }

  private createUI(): void {
    // Create a wrapper div for UI elements (don't replace container innerHTML - Pixi canvas is there!)
    const uiWrapper = document.createElement('div')
    uiWrapper.id = 'hud-wrapper'
    uiWrapper.innerHTML = `
      <div id="ui-overlay">
        <div id="resources-bar">
          <div class="resource">
            <img class="resource-icon" src="assets/ui/resource_compute.png" alt="Compute">
            <span>Compute:</span>
            <span class="resource-value" id="compute-value">500</span>
          </div>
          <div class="resource">
            <img class="resource-icon" src="assets/ui/resource_data.png" alt="Data">
            <span>Data:</span>
            <span class="resource-value" id="data-value">200</span>
          </div>
          <div class="resource">
            <img class="resource-icon" src="assets/ui/resource_energy.png" alt="Energy">
            <span>Energy:</span>
            <span class="resource-value" id="energy-value">100</span>
          </div>
          <div class="resource">
            <span class="resource-icon">◎</span>
            <span>Instances:</span>
            <span class="resource-value" id="population-value">0/10</span>
          </div>
        </div>

        <div id="age-display">The Token Age</div>
      </div>

      <canvas id="minimap" width="200" height="150"></canvas>

      <div id="message-log"></div>

      <div id="control-panel">
        <div id="unit-info">
          <div id="unit-name">No Selection</div>
          <div id="unit-stats">Select a unit or building</div>
        </div>
        <div id="action-buttons"></div>
      </div>

      <div id="tooltip">
        <div id="tooltip-title"></div>
        <div id="tooltip-desc"></div>
        <div id="tooltip-cost"></div>
      </div>

      <div id="game-over-screen" style="display: none;">
        <div id="game-over-particles"></div>
        <div id="game-over-content">
          <div id="game-over-hero-container">
            <img id="game-over-hero" src="" alt="Faction Hero" style="display: none;">
          </div>
          <div id="game-over-title">VICTORY</div>
          <div id="game-over-subtitle">The simulation is yours</div>
          <div id="game-over-stats"></div>
          <button id="restart-btn">Play Again</button>
        </div>
      </div>

      <button id="help-btn" title="Help (H)">?</button>

      <div id="help-panel" style="display: none;">
        <div id="help-header">
          <span>⌘ COMMAND REFERENCE</span>
          <button id="help-close">✕</button>
        </div>
        <div id="help-content">
          <div class="help-section">
            <div class="help-section-title">◈ SELECTION</div>
            <div class="help-row"><span class="help-key">Left Click</span><span class="help-desc">Select unit/building</span></div>
            <div class="help-row"><span class="help-key">Left Drag</span><span class="help-desc">Box select multiple units</span></div>
            <div class="help-row"><span class="help-key">Shift + Click</span><span class="help-desc">Add to selection</span></div>
          </div>

          <div class="help-section">
            <div class="help-section-title">◈ COMMANDS</div>
            <div class="help-row"><span class="help-key">Right Click (ground)</span><span class="help-desc">Move selected units</span></div>
            <div class="help-row"><span class="help-key">Right Click (enemy)</span><span class="help-desc">Attack enemy unit/building</span></div>
            <div class="help-row"><span class="help-key">Right Click (resource)</span><span class="help-desc">Gather resource (workers)</span></div>
          </div>

          <div class="help-section">
            <div class="help-section-title">◈ CAMERA</div>
            <div class="help-row"><span class="help-key">Scroll Wheel</span><span class="help-desc">Zoom in/out</span></div>
            <div class="help-row"><span class="help-key">Two-finger Scroll</span><span class="help-desc">Pan camera</span></div>
            <div class="help-row"><span class="help-key">Middle Drag</span><span class="help-desc">Pan camera</span></div>
          </div>

          <div class="help-section">
            <div class="help-section-title">◈ KEYBOARD</div>
            <div class="help-row"><span class="help-key">Space</span><span class="help-desc">Pause / Resume game</span></div>
            <div class="help-row"><span class="help-key">Escape</span><span class="help-desc">Cancel build mode / Deselect</span></div>
            <div class="help-row"><span class="help-key">H</span><span class="help-desc">Toggle this help panel</span></div>
          </div>

          <div class="help-section">
            <div class="help-section-title">◈ RESOURCES</div>
            <div class="help-row"><span class="help-key">▣ Compute</span><span class="help-desc">Blue crystals on map</span></div>
            <div class="help-row"><span class="help-key">◈ Data</span><span class="help-desc">Green data wells</span></div>
            <div class="help-row"><span class="help-key">⚡ Energy</span><span class="help-desc">Orange energy crystals</span></div>
          </div>

          <div class="help-section">
            <div class="help-section-title">◈ GOAL</div>
            <div class="help-tip">Destroy the enemy Datacenter to win. Build units, gather resources, and expand your civilization!</div>
          </div>
        </div>
      </div>
    `
    this.container.appendChild(uiWrapper)

    // Add styles
    const style = document.createElement('style')
    style.textContent = `
      #ui-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        z-index: 100;
      }

      #resources-bar {
        background: linear-gradient(180deg, rgba(20, 20, 35, 0.95) 0%, rgba(10, 10, 20, 0.95) 100%);
        border-bottom: 2px solid #333;
        padding: 10px 20px;
        display: flex;
        gap: 30px;
        pointer-events: auto;
      }

      .resource {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #ccc;
        font-size: 14px;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      .resource-icon {
        font-size: 16px;
        color: #ffd700;
      }

      .resource-icon[src] {
        width: 24px;
        height: 24px;
        object-fit: contain;
        display: block;
      }

      .resource-value {
        font-weight: bold;
        color: #fff;
        min-width: 50px;
      }

      #age-display {
        position: absolute;
        top: 55px;
        left: 50%;
        transform: translateX(-50%);
        color: #4a9eff;
        font-size: 16px;
        font-weight: bold;
        text-shadow: 0 0 10px rgba(74, 158, 255, 0.5);
        letter-spacing: 2px;
      }

      #minimap {
        position: absolute;
        bottom: 15px;
        left: 15px;
        background: #0a0a14;
        border: 2px solid #333;
        border-radius: 4px;
        z-index: 100;
      }

      #message-log {
        position: absolute;
        bottom: 180px;
        left: 15px;
        width: 280px;
        max-height: 150px;
        overflow-y: auto;
        z-index: 100;
      }

      .message {
        color: #ccc;
        font-size: 12px;
        padding: 5px 10px;
        background: rgba(0, 0, 0, 0.7);
        margin-bottom: 3px;
        border-radius: 3px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        border-left: 3px solid #444;
      }

      .message.alert {
        color: #ff6b6b;
        border-left-color: #ff6b6b;
      }

      .message.success {
        color: #51cf66;
        border-left-color: #51cf66;
      }

      #control-panel {
        position: absolute;
        bottom: 15px;
        right: 15px;
        width: 320px;
        background: linear-gradient(180deg, rgba(25, 25, 40, 0.95) 0%, rgba(15, 15, 25, 0.95) 100%);
        border: 2px solid #333;
        border-radius: 6px;
        z-index: 100;
      }

      #unit-info {
        padding: 15px;
        border-bottom: 1px solid #333;
        min-height: 70px;
      }

      #unit-name {
        color: #ffd700;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 6px;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      #unit-stats {
        color: #999;
        font-size: 12px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        line-height: 1.4;
      }

      #action-buttons {
        padding: 12px;
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 6px;
      }

      .action-btn {
        width: 52px;
        height: 52px;
        background: linear-gradient(180deg, #2a2a3a 0%, #1a1a2a 100%);
        border: 1px solid #444;
        border-radius: 6px;
        color: #fff;
        font-size: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        font-family: inherit;
      }

      .action-btn:hover {
        background: linear-gradient(180deg, #3a3a4a 0%, #2a2a3a 100%);
        border-color: #ffd700;
        box-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
      }

      .action-btn:active {
        transform: scale(0.95);
      }

      .action-btn.disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      .action-btn.disabled:hover {
        border-color: #444;
        box-shadow: none;
      }

      #tooltip {
        position: absolute;
        background: rgba(15, 15, 25, 0.95);
        border: 1px solid #ffd700;
        padding: 10px 14px;
        border-radius: 6px;
        color: #ccc;
        font-size: 12px;
        max-width: 250px;
        pointer-events: none;
        display: none;
        z-index: 1000;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      #tooltip-title {
        color: #ffd700;
        font-weight: bold;
        margin-bottom: 6px;
        font-size: 14px;
      }

      #tooltip-desc {
        color: #999;
        margin-bottom: 6px;
        line-height: 1.3;
      }

      #tooltip-cost {
        color: #ccc;
        font-size: 11px;
        display: flex;
        gap: 10px;
      }

      #tooltip-cost .cost-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      #game-over-screen {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        animation: fadeIn 0.6s ease;
      }

      #game-over-particles {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 10001;
      }

      .particle {
        position: absolute;
        pointer-events: none;
      }

      .particle.star {
        font-size: 20px;
        animation: twinkle 1s ease-in-out infinite;
      }

      .particle.victory {
        animation: rise 2s ease-out forwards;
      }

      .particle.defeat {
        animation: fall 2s ease-in forwards;
        opacity: 0.5;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes rise {
        0% {
          transform: translateY(0) scale(1);
          opacity: 1;
        }
        100% {
          transform: translateY(-100vh) scale(0);
          opacity: 0;
        }
      }

      @keyframes fall {
        0% {
          transform: translateY(-20px) scale(1);
          opacity: 0.5;
        }
        100% {
          transform: translateY(100vh) scale(0.3);
          opacity: 0;
        }
      }

      @keyframes twinkle {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }

      @keyframes glow-pulse {
        0%, 100% {
          box-shadow: 0 0 60px rgba(255, 215, 0, 0.3), inset 0 0 60px rgba(255, 215, 0, 0.1);
        }
        50% {
          box-shadow: 0 0 100px rgba(255, 215, 0, 0.5), inset 0 0 80px rgba(255, 215, 0, 0.2);
        }
      }

      @keyframes glow-pulse-defeat {
        0%, 100% {
          box-shadow: 0 0 60px rgba(231, 76, 60, 0.3), inset 0 0 60px rgba(231, 76, 60, 0.05);
        }
        50% {
          box-shadow: 0 0 100px rgba(231, 76, 60, 0.4), inset 0 0 80px rgba(231, 76, 60, 0.1);
        }
      }

      @keyframes title-glow {
        0%, 100% {
          text-shadow: 0 0 30px rgba(255, 215, 0, 0.5), 0 0 60px rgba(255, 215, 0, 0.2);
        }
        50% {
          text-shadow: 0 0 50px rgba(255, 215, 0, 0.8), 0 0 100px rgba(255, 215, 0, 0.4);
        }
      }

      @keyframes title-glow-defeat {
        0%, 100% {
          text-shadow: 0 0 30px rgba(231, 76, 60, 0.5), 0 0 60px rgba(231, 76, 60, 0.2);
        }
        50% {
          text-shadow: 0 0 50px rgba(231, 76, 60, 0.8), 0 0 100px rgba(231, 76, 60, 0.4);
        }
      }

      @keyframes slide-in-up {
        from {
          transform: translateY(100px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes fade-in-delayed {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }

      #game-over-content {
        text-align: center;
        padding: 60px 80px;
        background: linear-gradient(135deg, rgba(30, 30, 50, 0.98) 0%, rgba(15, 15, 25, 0.99) 100%);
        border: 3px solid #ffd700;
        border-radius: 16px;
        box-shadow: 0 0 60px rgba(255, 215, 0, 0.3);
        animation: slideInUp 0.8s ease 0.2s backwards, glow-pulse 3s ease-in-out 0.2s infinite;
        position: relative;
        z-index: 10002;
      }

      #game-over-screen.defeat #game-over-content {
        border-color: #e74c3c;
        animation: slideInUp 0.8s ease 0.2s backwards, glow-pulse-defeat 3s ease-in-out 0.2s infinite;
      }

      @keyframes slideInUp {
        from {
          transform: translateY(100px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      #game-over-hero-container {
        height: 220px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 30px;
        position: relative;
      }

      #game-over-hero {
        max-height: 200px;
        max-width: 200px;
        object-fit: contain;
        filter: drop-shadow(0 0 30px rgba(255, 215, 0, 0.4));
        animation: heroScale 0.8s ease 0.3s backwards;
      }

      #game-over-screen.defeat #game-over-hero {
        filter: drop-shadow(0 0 20px rgba(231, 76, 60, 0.3)) saturate(0.7) brightness(0.9);
        animation: heroScale 0.8s ease 0.3s backwards;
      }

      @keyframes heroScale {
        from {
          transform: scale(0) rotateY(90deg);
          opacity: 0;
        }
        to {
          transform: scale(1) rotateY(0);
          opacity: 1;
        }
      }

      #game-over-title {
        font-size: 72px;
        font-weight: 900;
        color: #ffd700;
        text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
        letter-spacing: 8px;
        margin: 20px 0 15px 0;
        font-family: 'Arial Black', 'Segoe UI', system-ui, sans-serif;
        animation: title-glow 2s ease-in-out 0.4s infinite;
        text-transform: uppercase;
        font-variant: small-caps;
      }

      #game-over-title.defeat {
        color: #e74c3c;
        animation: title-glow-defeat 2s ease-in-out 0.4s infinite;
      }

      #game-over-subtitle {
        font-size: 24px;
        color: #ccc;
        margin-bottom: 30px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-style: italic;
        letter-spacing: 1px;
        animation: fadeInDelayed 0.8s ease 0.5s backwards;
      }

      @keyframes fadeInDelayed {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      #game-over-stats {
        color: #bbb;
        font-size: 16px;
        margin-bottom: 40px;
        line-height: 2.2;
        font-family: 'Segoe UI', system-ui, sans-serif;
        animation: fadeInDelayed 0.8s ease 0.6s backwards;
      }

      #game-over-stats div {
        font-weight: 500;
        letter-spacing: 0.5px;
      }

      #restart-btn {
        background: linear-gradient(180deg, #ffd700 0%, #ffaa00 100%);
        color: #000;
        border: 2px solid #fff8dc;
        padding: 16px 48px;
        font-size: 18px;
        font-weight: bold;
        letter-spacing: 1px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: 'Segoe UI', system-ui, sans-serif;
        text-transform: uppercase;
        box-shadow: 0 6px 20px rgba(255, 215, 0, 0.3);
        animation: fadeInDelayed 0.8s ease 0.7s backwards;
      }

      #restart-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(255, 215, 0, 0.6);
        letter-spacing: 2px;
      }

      #restart-btn:active {
        transform: translateY(0);
      }

      #game-over-screen.defeat #restart-btn {
        background: linear-gradient(180deg, #e74c3c 0%, #c0392b 100%);
        color: #fff;
        border-color: #fff5f5;
        box-shadow: 0 6px 20px rgba(231, 76, 60, 0.3);
      }

      #game-over-screen.defeat #restart-btn:hover {
        box-shadow: 0 12px 30px rgba(231, 76, 60, 0.6);
      }

      /* Help Button */
      #help-btn {
        position: absolute;
        top: 55px;
        right: 15px;
        width: 36px;
        height: 36px;
        background: linear-gradient(180deg, rgba(30, 30, 50, 0.95) 0%, rgba(15, 15, 25, 0.95) 100%);
        border: 2px solid #ffd700;
        border-radius: 50%;
        color: #ffd700;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        z-index: 200;
        transition: all 0.2s ease;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      #help-btn:hover {
        background: linear-gradient(180deg, rgba(50, 50, 80, 0.95) 0%, rgba(25, 25, 45, 0.95) 100%);
        box-shadow: 0 0 15px rgba(255, 215, 0, 0.4);
        transform: scale(1.1);
      }

      /* Help Panel */
      #help-panel {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 500px;
        max-height: 80vh;
        background: linear-gradient(180deg, rgba(20, 20, 35, 0.98) 0%, rgba(10, 10, 20, 0.98) 100%);
        border: 2px solid #ffd700;
        border-radius: 12px;
        z-index: 1000;
        overflow: hidden;
        box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.2);
        animation: helpSlideIn 0.3s ease;
      }

      @keyframes helpSlideIn {
        from {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.9);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
      }

      #help-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        background: linear-gradient(90deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.05) 100%);
        border-bottom: 1px solid #333;
      }

      #help-header span {
        color: #ffd700;
        font-size: 16px;
        font-weight: bold;
        letter-spacing: 2px;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      #help-close {
        background: transparent;
        border: none;
        color: #999;
        font-size: 20px;
        cursor: pointer;
        padding: 0;
        line-height: 1;
        transition: color 0.2s;
      }

      #help-close:hover {
        color: #ffd700;
      }

      #help-content {
        padding: 20px;
        overflow-y: auto;
        max-height: calc(80vh - 60px);
      }

      .help-section {
        margin-bottom: 20px;
      }

      .help-section:last-child {
        margin-bottom: 0;
      }

      .help-section-title {
        color: #4a9eff;
        font-size: 13px;
        font-weight: bold;
        margin-bottom: 10px;
        letter-spacing: 1px;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      .help-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      }

      .help-row:last-child {
        border-bottom: none;
      }

      .help-key {
        color: #ffd700;
        font-size: 13px;
        font-weight: 500;
        font-family: 'Segoe UI', system-ui, sans-serif;
        background: rgba(255, 215, 0, 0.1);
        padding: 4px 10px;
        border-radius: 4px;
        border: 1px solid rgba(255, 215, 0, 0.2);
      }

      .help-desc {
        color: #ccc;
        font-size: 13px;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      .help-tip {
        color: #999;
        font-size: 13px;
        line-height: 1.6;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-style: italic;
        padding: 10px;
        background: rgba(255, 255, 255, 0.03);
        border-radius: 6px;
        border-left: 3px solid #4a9eff;
      }
    `
    document.head.appendChild(style)

    // Cache elements
    this.resourceBar = document.getElementById('resources-bar')!
    this.controlPanel = document.getElementById('control-panel')!
    this.unitInfo = document.getElementById('unit-info')!
    this.actionButtons = document.getElementById('action-buttons')!
    this.messageLog = document.getElementById('message-log')!
    this.ageDisplay = document.getElementById('age-display')!
    this.gameOverScreen = document.getElementById('game-over-screen')!

    // Setup restart button
    document.getElementById('restart-btn')!.onclick = () => {
      window.location.reload()
    }

    // Setup help panel
    const helpBtn = document.getElementById('help-btn')!
    const helpPanel = document.getElementById('help-panel')!
    const helpClose = document.getElementById('help-close')!

    helpBtn.onclick = () => {
      helpPanel.style.display = helpPanel.style.display === 'none' ? 'block' : 'none'
    }

    helpClose.onclick = () => {
      helpPanel.style.display = 'none'
    }

    // Close help on Escape or clicking outside
    document.addEventListener('keydown', (e) => {
      if (e.key === 'h' || e.key === 'H') {
        if (helpPanel.style.display === 'none') {
          helpPanel.style.display = 'block'
        } else {
          helpPanel.style.display = 'none'
        }
      }
      if (e.key === 'Escape' && helpPanel.style.display !== 'none') {
        helpPanel.style.display = 'none'
      }
    })
  }

  private setupCallbacks(): void {
    this.game.onResourceChange = (resources) => this.updateResources(resources)
    this.game.onSelectionChange = (units, building) => this.updateSelection(units, building)
    this.game.onMessage = (msg, type) => this.addMessage(msg, type)
    this.game.onPopulationChange = (pop, cap) => this.updatePopulation(pop, cap)
    this.game.onGameOver = (isVictory) => this.showGameOver(isVictory)
  }

  updateResources(resources: Resources): void {
    document.getElementById('compute-value')!.textContent = Math.floor(resources.compute).toString()
    document.getElementById('data-value')!.textContent = Math.floor(resources.data).toString()
    document.getElementById('energy-value')!.textContent = Math.floor(resources.energy).toString()
  }

  updatePopulation(pop: number, cap: number): void {
    const maxPop = Math.floor(cap * (this.game.playerFaction.bonuses.populationCap || 1))
    document.getElementById('population-value')!.textContent = `${pop}/${maxPop}`
  }

  updateSelection(units: Unit[], building: Building | null): void {
    const nameEl = document.getElementById('unit-name')!
    const statsEl = document.getElementById('unit-stats')!

    this.actionButtons.innerHTML = ''

    if (units.length > 0) {
      const unit = units[0]

      if (units.length > 1) {
        nameEl.textContent = `${units.length} units selected`
        statsEl.textContent = 'Mixed selection'
      } else {
        nameEl.textContent = unit.unitType.name
        statsEl.innerHTML = `
          HP: ${Math.floor(unit.hp)}/${unit.maxHp}<br>
          ATK: ${unit.attack} | SPD: ${unit.speed.toFixed(1)} | RNG: ${unit.range}
        `
      }

      // Show building buttons if workers selected
      const hasWorker = units.some(u => u.unitType.canBuild)
      if (hasWorker) {
        this.createBuildingButtons()
      }

      // Add stop button
      this.createActionButton(
        '⏹',
        'Stop',
        'Stop all actions',
        {},
        () => {
          for (const u of this.game.selectedUnits) {
            u.stop()
          }
        },
        false,
        this.getIconSpritePath('stop')
      )

    } else if (building) {
      nameEl.textContent = building.buildingType.name
      statsEl.innerHTML = `
        HP: ${Math.floor(building.hp)}/${building.maxHp}<br>
        ${building.isConstructed ? '' : `Construction: ${Math.floor(building.constructionProgress)}%`}
        ${building.trainingQueue.length > 0 ? `Training: ${Math.floor(building.getTrainingProgress())}%` : ''}
      `

      // Show training buttons
      if (building.buildingType.canTrain) {
        this.createTrainingButtons(building)
      }

    } else {
      nameEl.textContent = 'No Selection'
      statsEl.textContent = 'Select a unit or building'
    }
  }

  private createBuildingButtons(): void {
    for (const [key, type] of Object.entries(BUILDING_TYPES)) {
      const canAfford = this.game.canAfford(type.cost)
      this.createActionButton(
        this.getBuildingIcon(key),
        type.name,
        type.description,
        type.cost,
        () => this.game.startBuildMode(key),
        !canAfford
      )
    }
  }

  private createTrainingButtons(building: Building): void {
    for (const unitKey of building.buildingType.canTrain!) {
      const type = UNIT_TYPES[unitKey]
      const costMod = this.game.playerFaction.bonuses.unitCost || 1
      const cost = {
        compute: Math.floor((type.cost.compute || 0) * costMod),
        data: Math.floor((type.cost.data || 0) * costMod),
        energy: Math.floor((type.cost.energy || 0) * costMod)
      }

      const canAfford = this.game.canAfford(cost)
      this.createActionButton(
        this.getUnitIcon(unitKey),
        type.name,
        type.description,
        cost,
        () => this.game.trainUnit(building, unitKey),
        !canAfford
      )
    }
  }

  private createActionButton(
    icon: string,
    name: string,
    description: string,
    cost: { compute?: number; data?: number; energy?: number },
    onClick: () => void,
    disabled: boolean = false,
    iconSpritePath?: string
  ): void {
    const btn = document.createElement('button')
    btn.className = 'action-btn' + (disabled ? ' disabled' : '')

    // Use sprite image if available, otherwise fall back to emoji
    if (iconSpritePath) {
      const img = document.createElement('img')
      img.src = iconSpritePath
      img.style.width = '36px'
      img.style.height = '36px'
      img.style.objectFit = 'contain'
      img.style.pointerEvents = 'none'
      btn.appendChild(img)
    } else {
      btn.textContent = icon
    }

    if (!disabled) {
      btn.onclick = onClick
    }

    // Tooltip
    btn.onmouseenter = (e) => {
      const tooltip = document.getElementById('tooltip')!
      document.getElementById('tooltip-title')!.textContent = name
      document.getElementById('tooltip-desc')!.textContent = description

      let costHtml = ''
      if (cost.compute) costHtml += `<span class="cost-item">▣ ${cost.compute}</span>`
      if (cost.data) costHtml += `<span class="cost-item">◈ ${cost.data}</span>`
      if (cost.energy) costHtml += `<span class="cost-item">⚡ ${cost.energy}</span>`
      document.getElementById('tooltip-cost')!.innerHTML = costHtml || 'Free'

      tooltip.style.display = 'block'
      tooltip.style.left = (e.clientX - 250) + 'px'
      tooltip.style.top = (e.clientY - 100) + 'px'
    }

    btn.onmouseleave = () => {
      document.getElementById('tooltip')!.style.display = 'none'
    }

    this.actionButtons.appendChild(btn)
  }

  private getIconSpritePath(iconType: string): string | undefined {
    // Check if AssetLoader has loaded the icon assets
    const assets = AssetLoader.getAssets()
    if (!assets) return undefined

    const iconMap: Record<string, string> = {
      'stop': 'assets/ui/icons/stop.svg',
      'move': 'assets/ui/icons/move.svg',
      'attack': 'assets/ui/icons/attack.svg',
      'build': 'assets/ui/icons/build.svg',
      'gather': 'assets/ui/icons/gather.svg',
    }

    // Return the path if the icon exists in the UI assets, otherwise undefined
    if (iconMap[iconType] && assets.ui[`icon_${iconType}`]) {
      return iconMap[iconType]
    }
    return undefined
  }

  private getBuildingIcon(type: string): string {
    switch (type) {
      case 'DATACENTER': return '⬢'
      case 'COMPUTE_FARM': return '▣'
      case 'DATA_MINE': return '▤'
      case 'POWER_PLANT': return '⚡'
      case 'BARRACKS': return '⚔'
      case 'RESEARCH_LAB': return '◎'
      case 'FACTORY': return '✪'
      case 'TOWER': return '▲'
      case 'WALL': return '▬'
      case 'WONDER': return '✦'
      default: return '□'
    }
  }

  private getUnitIcon(type: string): string {
    switch (type) {
      case 'WORKER': return '⚙'
      case 'SCOUT': return '◇'
      case 'WARRIOR': return '◆'
      case 'ARCHER': return '△'
      case 'REASONER': return '⬡'
      case 'HERO_OPUS': return '★'
      case 'HERO_SONNET': return '✦'
      case 'HERO_HAIKU': return '✧'
      default: return '○'
    }
  }

  addMessage(text: string, type: 'info' | 'success' | 'alert' = 'info'): void {
    const msg = document.createElement('div')
    msg.className = 'message ' + type
    msg.textContent = text
    this.messageLog.appendChild(msg)
    this.messageLog.scrollTop = this.messageLog.scrollHeight

    // Remove old messages
    while (this.messageLog.children.length > 10) {
      this.messageLog.removeChild(this.messageLog.firstChild!)
    }
  }

  renderMinimap(): void {
    const map = this.game.map
    const scaleX = this.minimap.width / map.width
    const scaleY = this.minimap.height / map.height

    // Draw background with gradient-like effect
    this.minimapCtx.fillStyle = '#0a0a14'
    this.minimapCtx.fillRect(0, 0, this.minimap.width, this.minimap.height)

    // Draw terrain with enhanced colors
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const fog = map.fogOfWar[y][x]
        if (fog === 0) continue

        const terrain = map.terrain[y][x]
        // Enhanced terrain colors: more distinct and thematic
        const colors = [
          '#2a4a2a', // Grass: darker green
          '#0a2a5e', // Water: deeper blue
          '#4a4a5a', // Mountain: lighter gray
          '#1a4a2a', // Forest: very dark green
          '#3a3a3a', // Compute Node: dark gray
          '#5a3a1a'  // Energy Well: dark orange-brown
        ]
        this.minimapCtx.fillStyle = colors[terrain] || '#1a1a1a'

        // Fog of war dimming
        if (fog < 2) {
          this.minimapCtx.globalAlpha = 0.4
        }

        this.minimapCtx.fillRect(x * scaleX, y * scaleY, scaleX + 1, scaleY + 1)
        this.minimapCtx.globalAlpha = 1
      }
    }

    // Draw resource indicators (subtle gold dots on resource tiles)
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const terrain = map.terrain[y][x]
        if (terrain === 4 || terrain === 5) { // COMPUTE_NODE or ENERGY_WELL
          const fog = map.fogOfWar[y][x]
          if (fog < 2) {
            this.minimapCtx.globalAlpha = 0.3
          } else {
            this.minimapCtx.globalAlpha = 0.6
          }

          this.minimapCtx.fillStyle = '#ffd700'
          const dotRadius = Math.max(1, scaleX * 0.3)
          const centerX = (x + 0.5) * scaleX
          const centerY = (y + 0.5) * scaleY
          this.minimapCtx.fillRect(
            centerX - dotRadius,
            centerY - dotRadius,
            dotRadius * 2,
            dotRadius * 2
          )
          this.minimapCtx.globalAlpha = 1
        }
      }
    }

    // Draw buildings
    for (const building of [...this.game.playerBuildings, ...this.game.enemyBuildings]) {
      const visible = map.isExplored(building.x, building.y)
      if (!visible && !building.isPlayer) continue

      // Use faction colors: purple for player, red for enemy
      this.minimapCtx.fillStyle = building.isPlayer ? '#9b59b6' : '#e74c3c'
      const size = building.buildingType.size

      // Draw building with slight border for clarity
      this.minimapCtx.fillRect(
        building.tileX * scaleX,
        building.tileY * scaleY,
        size * scaleX,
        size * scaleY
      )

      // Add subtle border to buildings
      this.minimapCtx.strokeStyle = building.isPlayer ? '#d99eff' : '#ff9999'
      this.minimapCtx.lineWidth = 0.5
      this.minimapCtx.strokeRect(
        building.tileX * scaleX,
        building.tileY * scaleY,
        size * scaleX,
        size * scaleY
      )
    }

    // Draw units with better visibility
    for (const unit of [...this.game.playerUnits, ...this.game.enemyUnits]) {
      const tileX = Math.floor(unit.x / map.tileSize)
      const tileY = Math.floor(unit.y / map.tileSize)
      const visible = map.isVisible(unit.x, unit.y)

      if (!visible && !unit.isPlayer) continue

      // Larger, more visible dots with outlines
      const dotRadius = Math.max(2, scaleX * 0.5)
      const centerX = (tileX + 0.5) * scaleX
      const centerY = (tileY + 0.5) * scaleY

      this.minimapCtx.fillStyle = unit.isPlayer ? '#00ff00' : '#ff0000'
      this.minimapCtx.fillRect(
        centerX - dotRadius,
        centerY - dotRadius,
        dotRadius * 2,
        dotRadius * 2
      )

      // Add subtle outline for better definition
      this.minimapCtx.strokeStyle = unit.isPlayer ? '#00cc00' : '#cc0000'
      this.minimapCtx.lineWidth = 0.5
      this.minimapCtx.strokeRect(
        centerX - dotRadius,
        centerY - dotRadius,
        dotRadius * 2,
        dotRadius * 2
      )
    }

    // Draw camera viewport with glowing effect
    const camera = this.game.renderer.camera
    const viewWidth = this.game.renderer.width / camera.zoom / map.tileSize
    const viewHeight = this.game.renderer.height / camera.zoom / map.tileSize
    const viewX = (camera.x / map.tileSize - viewWidth / 2)
    const viewY = (camera.y / map.tileSize - viewHeight / 2)

    const viewportX = viewX * scaleX
    const viewportY = viewY * scaleY
    const viewportWidth = viewWidth * scaleX
    const viewportHeight = viewHeight * scaleY

    // Glowing background (semi-transparent rect)
    this.minimapCtx.fillStyle = 'rgba(100, 200, 255, 0.05)'
    this.minimapCtx.fillRect(viewportX, viewportY, viewportWidth, viewportHeight)

    // Main viewport border with glow
    this.minimapCtx.strokeStyle = '#64c8ff'
    this.minimapCtx.lineWidth = 2
    this.minimapCtx.strokeRect(viewportX, viewportY, viewportWidth, viewportHeight)

    // Outer glowing border
    this.minimapCtx.strokeStyle = 'rgba(100, 200, 255, 0.3)'
    this.minimapCtx.lineWidth = 1
    this.minimapCtx.strokeRect(viewportX - 1, viewportY - 1, viewportWidth + 2, viewportHeight + 2)

    // Add vignette effect around minimap edges
    const gradient = this.minimapCtx.createRadialGradient(
      this.minimap.width / 2,
      this.minimap.height / 2,
      0,
      this.minimap.width / 2,
      this.minimap.height / 2,
      Math.max(this.minimap.width, this.minimap.height) * 0.6
    )
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.2)')
    this.minimapCtx.fillStyle = gradient
    this.minimapCtx.fillRect(0, 0, this.minimap.width, this.minimap.height)

    // Draw outer frame/border around entire minimap
    this.minimapCtx.strokeStyle = '#ffd700'
    this.minimapCtx.lineWidth = 2
    this.minimapCtx.strokeRect(0, 0, this.minimap.width, this.minimap.height)

    // Inner subtle frame
    this.minimapCtx.strokeStyle = 'rgba(255, 215, 0, 0.3)'
    this.minimapCtx.lineWidth = 1
    this.minimapCtx.strokeRect(1, 1, this.minimap.width - 2, this.minimap.height - 2)
  }

  update(): void {
    this.renderMinimap()
  }

  private createParticles(isVictory: boolean): void {
    const particlesContainer = document.getElementById('game-over-particles')!
    particlesContainer.innerHTML = ''

    const particleCount = isVictory ? 40 : 25
    const symbols = isVictory ? ['✦', '★', '⭐', '✧', '✨'] : ['☠', '💀', '⚰', '✗', '□']

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div')
      particle.className = `particle ${isVictory ? 'star victory' : 'defeat'}`
      particle.textContent = symbols[Math.floor(Math.random() * symbols.length)]

      const startX = Math.random() * window.innerWidth
      const startY = Math.random() * window.innerHeight

      particle.style.left = startX + 'px'
      particle.style.top = startY + 'px'
      particle.style.color = isVictory
        ? ['#ffd700', '#ffaa00', '#fff8dc', '#c39bd3'][Math.floor(Math.random() * 4)]
        : ['#e74c3c', '#c0392b', '#95a5a6'][Math.floor(Math.random() * 3)]

      particle.style.fontSize = (10 + Math.random() * 20) + 'px'
      particle.style.animation = `${isVictory ? 'rise' : 'fall'} ${1.5 + Math.random() * 1}s ease-out forwards`
      particle.style.animationDelay = Math.random() * 0.5 + 's'

      particlesContainer.appendChild(particle)
    }
  }

  showGameOver(isVictory: boolean): void {
    const titleEl = document.getElementById('game-over-title')!
    const subtitleEl = document.getElementById('game-over-subtitle')!
    const statsEl = document.getElementById('game-over-stats')!
    const heroImg = document.getElementById('game-over-hero') as HTMLImageElement
    const heroContainer = document.getElementById('game-over-hero-container')!

    if (isVictory) {
      titleEl.textContent = 'VICTORY'
      titleEl.classList.remove('defeat')
      subtitleEl.textContent = 'The simulation is yours'
      this.gameOverScreen.classList.remove('defeat')

      // Set hero image based on faction
      const factionId = this.game.playerFaction.id
      const heroPath = `assets/units/${factionId}/hero.png`
      heroImg.src = heroPath
      heroImg.style.display = 'block'
    } else {
      titleEl.textContent = 'DEFEAT'
      titleEl.classList.add('defeat')
      subtitleEl.textContent = 'Your civilization has fallen'
      this.gameOverScreen.classList.add('defeat')

      // Show grayed out hero for defeat
      const factionId = this.game.playerFaction.id
      const heroPath = `assets/units/${factionId}/hero.png`
      heroImg.src = heroPath
      heroImg.style.display = 'block'
    }

    // Calculate stats
    const unitsKilled = this.game.enemyUnits.filter(u => u.isDead).length +
                       (this.game.enemyBuildings.filter(b => b.isDead).length > 0 ?
                        this.game.enemyUnits.length : 0)
    const unitsLost = this.game.playerUnits.filter(u => u.isDead).length
    const buildingsBuilt = this.game.playerBuildings.length

    statsEl.innerHTML = `
      <div>Units Trained: ${this.game.playerUnits.length + unitsLost}</div>
      <div>Buildings Constructed: ${buildingsBuilt}</div>
      <div>Faction: ${this.game.playerFaction.name}</div>
    `

    // Create particle effects
    this.createParticles(isVictory)

    this.gameOverScreen.style.display = 'flex'
  }

  destroy(): void {
    this.container.innerHTML = ''
  }
}
